import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const FormTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormTileHeader = styled.div<{ $color?: string }>`
  width: 100%;
  background: ${(props) => props.$color || "#f8f9fa"};
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #eee;
  margin-bottom: 1rem;

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: white;
    margin: 0;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const FormTileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const FormInput = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.$hasError ? "#d32f2f" : "#ddd")};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? "#d32f2f" : "#013566")};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.$hasError ? "rgba(211, 47, 47, 0.2)" : "rgba(1, 53, 102, 0.2)"};
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const FieldError = styled.span`
  font-size: 0.85rem;
  color: #d32f2f;
`;

const getPhoneDigits = (value: string): string => {
  let digits = value.replace(/\D/g, "");
  // Strip leading US country code if present (11 digits starting with 1)
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
};

const formatUsPhoneNumber = (value: string): string => {
  const digits = getPhoneDigits(value);

  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isValidUsPhoneNumber = (value: string): boolean => {
  const digits = getPhoneDigits(value);
  return digits.length === 0 || digits.length === 10;
};

const FormSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 0.75rem 2rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Props {
  setSnackbar: any;
}

const EditCampaign: React.FC<Props> = ({ setSnackbar }) => {
  const { id, campaignId } = useParams<{ id: string; campaignId: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [campaignName, setCampaignName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [trafficSource, setTrafficSource] = useState("Email");
  const [status, setStatus] = useState("Active");
  const [cpl, setCpl] = useState("");
  const [cpm, setCpm] = useState("");
  const [vertical, setVertical] = useState("");
  const [verticals, setVerticals] = useState<any[]>([]);
  const [loadingVerticals, setLoadingVerticals] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch verticals for dropdown
  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        setLoadingVerticals(true);
        const response = await axios.get(API_ENDPOINTS.VERTICALS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setVerticals(response.data || []);
      } catch (error) {
        console.error("Error fetching verticals:", error);
        setSnackbar({
          isOpen: true,
          message: "Failed to load verticals",
          type: "error",
        });
      } finally {
        setLoadingVerticals(false);
      }
    };

    fetchVerticals();
  }, [token, setSnackbar]);

  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!campaignId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          API_ENDPOINTS.CAMPAIGN_DETAILS(campaignId),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const campaign = response.data;
        if (campaign) {
          setCampaignName(campaign.name || "");
          setPhoneNumber(formatUsPhoneNumber(campaign.phone_number || ""));
          // Map API traffic_source (lowercase) to dropdown option value
          const trafficSourceMap: Record<string, string> = {
            email: "Email",
            social: "Social",
            listicle: "Listicle",
            search: "Search",
            "host & post": "Host & Post",
            sms: "SMS",
          };
          const raw = campaign.traffic_source?.toLowerCase()?.trim() || "";
          const trafficSourceDisplay =
            trafficSourceMap[raw] ||
            (raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Email");
          setTrafficSource(trafficSourceDisplay);
          setStatus(campaign.status === 1 ? "Active" : "Inactive");
          setCpl(campaign.cp?.toString() || "");
          setCpm(campaign.cpm?.toString() || "");
          setVertical(campaign.vertical || "");
        }
      } catch (error) {
        console.error("Error fetching campaign data:", error);
        setSnackbar({
          isOpen: true,
          message: "Failed to load campaign data",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [campaignId, token, setSnackbar]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatUsPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setPhoneError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignId) {
      setSnackbar({
        isOpen: true,
        message: "Campaign ID is missing",
        type: "error",
      });
      return;
    }

    if (!isValidUsPhoneNumber(phoneNumber)) {
      setPhoneError("Please enter a valid 10-digit US phone number.");
      return;
    }

    setSaving(true);

    try {
      // Convert status from "Active"/"Inactive" to 1/0
      const statusValue = status === "Active" ? 1 : 0;

      // Convert traffic_source to lowercase (API expects lowercase)
      const trafficSourceLower = trafficSource.toLowerCase();

      // Prepare request body
      const requestData: any = {
        name: campaignName,
        traffic_source: trafficSourceLower,
        status: statusValue,
        cp: cpl,
        cpm: cpm,
        vertical: vertical,
      };

      const phoneDigits = getPhoneDigits(phoneNumber);
      if (phoneDigits) {
        // API expects 11 characters: US country code + 10-digit number
        requestData.phone_number =
          phoneDigits.length === 10 ? `1${phoneDigits}` : phoneDigits;
      }

      await axios.put(API_ENDPOINTS.CAMPAIGN_DETAILS(campaignId), requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setSnackbar({
        isOpen: true,
        message: "Campaign updated successfully!",
        type: "success",
      });

      // Navigate back to vendor details after a short delay
      setTimeout(() => {
        navigate(`/vendors/${id}`);
      }, 1000);
    } catch (err: any) {
      console.error("Error updating campaign:", err);
      setSnackbar({
        isOpen: true,
        message:
          err.response?.data?.message ||
          "Failed to update campaign. Please try again.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/vendors/${id}`);
  };

  return (
    <Container>
      <PageTitle>Edit Campaign</PageTitle>
      <GoBack onClick={() => navigate(`/vendors/${id}`)}>
        Back to Vendor Details
      </GoBack>

      <FormContainer>
        <FormTile>
          <FormTileHeader $color="#B8860B">
            <h2>Campaign Details</h2>
          </FormTileHeader>
          <FormTileSection>
            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                Loading campaign data...
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel htmlFor="campaignName">Campaign Name</FormLabel>
                  <FormInput
                    type="text"
                    id="campaignName"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Enter campaign name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="phoneNumber">Phone Number</FormLabel>
                  <FormInput
                    type="tel"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    inputMode="numeric"
                    maxLength={14}
                    $hasError={!!phoneError}
                  />
                  {phoneError && <FieldError>{phoneError}</FieldError>}
                </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="vertical">Vertical</FormLabel>
              <FormSelect
                id="vertical"
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                disabled={loadingVerticals}
              >
                <option value="">
                  {loadingVerticals ? "Loading verticals..." : "Select a vertical"}
                </option>
                {verticals.map((v) => (
                  <option key={v.id} value={v.name}>
                    {v.name
                      ? v.name.charAt(0).toUpperCase() + v.name.slice(1)
                      : `Vertical ${v.id}`}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="trafficSource">Traffic Source</FormLabel>
                  <FormSelect
                    id="trafficSource"
                    value={trafficSource}
                    onChange={(e) => setTrafficSource(e.target.value)}
                    required
                  >
                    <option value="Email">Email</option>
                    <option value="Social">Social</option>
                    <option value="Listicle">Listicle</option>
                    <option value="Search">Search</option>
                    <option value="Host & Post">Host & Post</option>
                    <option value="SMS">SMS</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="status">Status</FormLabel>
                  <FormSelect
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    required
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="cpl">CPL</FormLabel>
                  <FormInput
                    type="text"
                    id="cpl"
                    value={cpl}
                    onChange={(e) => setCpl(e.target.value)}
                    placeholder="Enter CPL"
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="cpm">CPM</FormLabel>
                  <FormInput
                    type="text"
                    id="cpm"
                    value={cpm}
                    onChange={(e) => setCpm(e.target.value)}
                    placeholder="Enter CPM"
                  />
                </FormGroup>

                <ButtonGroup>
                  <Button
                    type="button"
                    $variant="secondary"
                    onClick={handleCancel}
                    disabled={saving || loading}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving || loading}>
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </ButtonGroup>
              </form>
            )}
          </FormTileSection>
        </FormTile>
      </FormContainer>
    </Container>
  );
};

export default EditCampaign;
